<script setup>
import { ref, computed, watch } from "vue";
import { useNoteStore } from "@/stores/useNoteStore";
import { useActivityStore } from "@/stores/useActivityStore";
import DeleteConfirmModal from "@/components/Generics/DeleteConfirmModal.vue";

const props = defineProps({
  noteId: { type: Number, required: true },
});

const emit = defineEmits(["close", "refresh", "notify"]);

const noteStore = useNoteStore();
const activityStore = useActivityStore();

const isDeleting = ref(false);

// Nota atual (pode sumir/mudar após deletar)
const note = computed(() =>
  noteStore.notes.find((n) => Number(n.noteId) === Number(props.noteId))
);

// Congela o título e o contexto antes do delete
const frozenTitle = ref("");
const frozenActivityId = ref(null);

function freezeTitle() {
  frozenTitle.value = note.value?.title?.trim?.() || "";
}

function freezeDeleteContext() {
  freezeTitle();

  frozenActivityId.value =
    note.value?.activityId ?? activityStore.idActivity ?? null;
}

// Atualiza congelado enquanto NÃO está deletando
watch(
  () => note.value?.title,
  () => {
    if (!isDeleting.value) freezeTitle();
  },
  { immediate: true }
);

const modalTitle = "Apagar Nota";

const messageTitle = computed(() => {
  const title = frozenTitle.value;
  return title
    ? `Tem certeza que deseja apagar a nota "${title}"?`
    : "Tem certeza que deseja apagar a nota?";
});

const messageSubtitle = computed(() => "Esta ação não pode ser desfeita.");

function buildErrorMessage(error) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.title ||
    error?.message ||
    "Ocorreu um erro ao excluir a nota."
  );
}

async function refreshActivityNotesSafely() {
  const activityId = frozenActivityId.value;

  // No NoteGeneralForm, especialmente em nota avulsa, pode não haver activityId.
  // Nesses casos, o pai é quem deve atualizar a lista geral.
  if (!activityId) return;

  try {
    // deleteNoteById (chamado em onConfirmDelete) já atualiza a lista paginada,
    // decidindo com base no notes.length pré-delete se deve voltar uma página.
    // Aqui só resincronizamos allNotes (fonte dos destaques em TextWithNotes.vue).
    await noteStore.getNotesByActivityId(activityId, true, true, false, []);

    // Corrige o caso raro em que a página ainda ficou vazia mesmo após o ajuste
    // feito por deleteNoteById — só refaz o fetch paginado quando necessário.
    if (noteStore.notes.length === 0 && noteStore.currentPage > 1) {
      noteStore.currentPage -= 1;
      await noteStore.fetchNotesByActivityIdPaged(
        activityId,
        noteStore.currentPage,
        noteStore.pageSize,
        noteStore.searchTerm
      );
    }

    await activityStore.getActivityById(activityId);
  } catch (refreshError) {
    // A exclusão já ocorreu. Uma falha de refresh contextual não pode
    // impedir o DeleteConfirmModal de emitir success/close.
    console.warn(
      "Nota excluída, mas houve falha ao atualizar a lista contextual:",
      refreshError
    );
  }
}

async function onConfirmDelete() {
  freezeDeleteContext();
  isDeleting.value = true;

  try {
    // 1) A única etapa que deve bloquear o sucesso real é a exclusão.
    await noteStore.deleteNoteById(props.noteId);
  } catch (error) {
    const msg = buildErrorMessage(error);

    emit("notify", {
      type: "error",
      message: msg,
    });

    isDeleting.value = false;
    throw new Error(msg);
  }

  // 2) Atualização contextual é best-effort. Se falhar, o pai ainda fecha
  // os modais e atualiza a tela atual pelo evento refresh.
  await refreshActivityNotesSafely();

  emit("notify", {
    type: "success",
    message: "Nota apagada com sucesso.",
  });

  // Não emite refresh/close aqui. Isso acontece no @success do
  // DeleteConfirmModal, disparado imediatamente (closeImmediatelyOnSuccess)
  // logo após este onConfirmDelete resolver — sem o toast interno do modal,
  // para não sobrepor com o toast contextual acima.
}

// Cancelamento (X ou botão Cancelar): só fecha se não estiver deletando
function handleClose() {
  if (isDeleting.value) return;
  emit("close");
}

// Sucesso: disparado pelo DeleteConfirmModal assim que onConfirmDelete resolve
// (closeImmediatelyOnSuccess=true, sem o toast interno do modal).
function handleSuccess() {
  isDeleting.value = false;
  emit("refresh");
  emit("close");
}
</script>

<template>
  <DeleteConfirmModal
    :show="true"
    :title="modalTitle"
    :messageTitle="messageTitle"
    :messageSubtitle="messageSubtitle"
    confirmText="Sim, apagar"
    cancelText="Cancelar"
    loadingText="Apagando..."
    :onConfirm="onConfirmDelete"
    :closeImmediatelyOnSuccess="true"
    @close="handleClose"
    @success="handleSuccess"
  />
</template>
